import { finalize, forkJoin, tap } from 'rxjs';

import { inject } from '@angular/core';

import { SpinnerService } from '@mitsubishi/ui/spinner';
import { setCompletedState, setErrorState, setPendingState, withRequestStatus } from '@mitsubishi/utils';

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import {
  AccopRegion,
  CombRequest,
  CombResponse,
  CsResponse,
  FoulingFactorVal,
  FreestFieldMappingDto,
  FreestFieldMappingMap,
  FrontEndLimit,
  MachineSeries,
  SoftwareData,
  StandardCondition,
  UomSystem,
} from '../models/chiller-free-st.model';
import { ChillerFreeStService } from '../services/chiller-free-st.service';

interface ChillerFreeStState {
  machineSeriesList: MachineSeries[];
  foulingFactorValList: FoulingFactorVal[];
  softwareData: SoftwareData | null;
  standardConditionList: StandardCondition[];
  accopRegionList: AccopRegion[];
  frontEndLimit: FrontEndLimit | null;
  compressorSeriesList: CsResponse[];
  compressorSeriesLoading: boolean;
  fieldMappingMap: FreestFieldMappingMap;
  siFoulingFactorMapping: FreestFieldMappingDto | null;
  uomSystem: UomSystem;
  combinationResults: CombResponse[];
  combinationsLoading: boolean;
  lastCombRequest: CombRequest | null;
}

const initialState: ChillerFreeStState = {
  machineSeriesList: [],
  foulingFactorValList: [],
  softwareData: null,
  standardConditionList: [],
  accopRegionList: [],
  frontEndLimit: null,
  compressorSeriesList: [],
  compressorSeriesLoading: false,
  fieldMappingMap: {},
  siFoulingFactorMapping: null,
  uomSystem: 'SI',
  combinationResults: [],
  combinationsLoading: false,
  lastCombRequest: null,
};

function buildFieldMappingMap(mappings: FreestFieldMappingDto[]): FreestFieldMappingMap {
  const map: FreestFieldMappingMap = {};
  for (const m of mappings) {
    if (!map[m.section]) map[m.section] = {};
    map[m.section][m.responseField] = m;
  }
  return map;
}

export const ChillerFreeStStore = signalStore(
  withState(initialState),
  withRequestStatus(),
  withMethods((store, service = inject(ChillerFreeStService), spinnerService = inject(SpinnerService)) => ({
    loadStart: (uomSystem: UomSystem = 'SI') => {
      patchState(store, setPendingState());
      spinnerService.showMainSpinner();

      forkJoin({
        start: service.getStart(),
        fieldMappings: service.getFieldMappings(uomSystem),
      })
        .pipe(finalize(() => {
          patchState(store, setCompletedState());
          spinnerService.hideMainSpinner();
        }))
        .subscribe({
          next: ({ start, fieldMappings }) => {
            const mappingMap = buildFieldMappingMap(fieldMappings);
            patchState(store, {
              machineSeriesList: start.machineSeriesList,
              foulingFactorValList: start.foulingFactorValList,
              softwareData: start.softwareData,
              standardConditionList: start.standardConditionList,
              accopRegionList: start.accopRegionList,
              frontEndLimit: start.frontEndLimit,
              fieldMappingMap: mappingMap,
              siFoulingFactorMapping:
                uomSystem === 'SI' ? mappingMap['EVAPORATOR']?.['evapWC.ffval'] ?? null : null,
              uomSystem,
            });
          },
          error: error => patchState(store, setErrorState(error.message)),
        });
    },
    loadCompressorSeries: (serieId: string) => {
      patchState(store, { compressorSeriesList: [], compressorSeriesLoading: true });

      service
        .getCompressorSeries(serieId)
        .pipe(finalize(() => patchState(store, { compressorSeriesLoading: false })))
        .subscribe({
          next: response => {
            patchState(store, { compressorSeriesList: response });
          },
        });
    },
    loadFieldMappings: (uomSystem: UomSystem) => {
      return service.getFieldMappings(uomSystem).pipe(
        tap(response => {
          patchState(store, {
            fieldMappingMap: buildFieldMappingMap(response),
            uomSystem,
          });
        })
      );
    },
    loadCombinations: (request: CombRequest) => {
      patchState(store, { combinationResults: [], combinationsLoading: true, lastCombRequest: request });
      spinnerService.showMainSpinner();

      service
        .getCombinations(request)
        .pipe(finalize(() => {
          patchState(store, { combinationsLoading: false });
          spinnerService.hideMainSpinner();
        }))
        .subscribe({
          next: response => {
            patchState(store, { combinationResults: response });
          },
        });
    },
  }))
);
